import _ from 'lodash';

import {
  getFullContent,
  HighlightableString,
  HighlightableStringPart,
  HighlightableStringTerm,
} from '../HighlightableString';
import { HighlighterId } from '../text/Highlighter';
import { FoundTextOccurance } from './findText';

interface Marker {
  startedTerms: HighlightableStringTerm[];
  endedTerms: HighlightableStringTerm[];

  emptyParts: HighlightableStringPart[];

  // Override previous CSS classes or keep them if undefined.
  cssClasses: string[] | undefined;
}

function getMarkerAt(parts: Map<number, Marker>, index: number): Marker {
  let part = parts.get(index);
  if (typeof part === 'undefined') {
    part = {
      startedTerms: [],
      endedTerms: [],
      emptyParts: [],
      cssClasses: undefined,
    };
    parts.set(index, part);
  }
  return part;
}

export function mergeTerms(
  s: HighlightableString,
  highlighterId: HighlighterId,
  foundOccurances: FoundTextOccurance[],
): HighlightableStringTerm[] {
  if (foundOccurances.length === 0) {
    return [];
  }

  if (typeof s.highlighted === 'undefined') {
    s.highlighted = {
      parts: _.cloneDeep(s.originalParts),
      terms: [],
    };
  }

  const markerPerIndex = new Map<number, Marker>();

  const fullContent = getFullContent(s);

  // Create beginning (before the first character) and ending (after the last character).
  getMarkerAt(markerPerIndex, 0);
  getMarkerAt(markerPerIndex, fullContent.length);

  const existingTerms = s.highlighted.terms;

  const addedTerms = foundOccurances.map((occurance) => {
    const newTerm: HighlightableStringTerm = {
      startIndex: occurance.startIndex,
      partIndices: [],
      partHtmlIds: [],
      content: fullContent.substring(occurance.startIndex, occurance.startIndex + occurance.length),
      highlightedBy: highlighterId,
    };
    return newTerm;
  });

  const newTerms = [existingTerms, addedTerms].flatMap((a) => a);

  newTerms.forEach((term) => {
    const startMarker = getMarkerAt(markerPerIndex, term.startIndex);
    startMarker.startedTerms.push(term);

    const endMarker = getMarkerAt(markerPerIndex, term.startIndex + term.content.length);
    endMarker.endedTerms.push(term);
  });

  s.originalParts.forEach((part) => {
    const marker = getMarkerAt(markerPerIndex, part.startIndex);
    marker.cssClasses = part.cssClasses || [];

    if (part.length === 0) {
      marker.emptyParts.push(part);
    }
  });

  const markerStartIndices = Array.from(markerPerIndex.keys()).sort((a, b) => a - b);
  const activeTerms = new Set<HighlightableStringTerm>();
  let activeCssClasses: string[];

  const newParts: HighlightableStringPart[] = [];
  let prevStartIndex = 0;

  markerStartIndices.forEach((startIndex) => {
    const currentMarker = markerPerIndex.get(startIndex)!;

    const isFirstPart = startIndex === 0;
    if (isFirstPart) {
      currentMarker.startedTerms.forEach((term) => {
        activeTerms.add(term);
      });

      activeCssClasses =
        typeof currentMarker.cssClasses !== 'undefined' ? _.clone(currentMarker.cssClasses) : [];
      return;
    }

    const allActiveTerms = Array.from(activeTerms.values());

    const part: HighlightableStringPart = {
      startIndex: prevStartIndex,
      length: startIndex - prevStartIndex,
      cssClasses: activeCssClasses.length > 0 ? activeCssClasses : undefined,
      highlighted:
        activeTerms.size > 0
          ? {
              htmlId: _.uniqueId('highlightablePart_'),
              highlightedBy: allActiveTerms.map((term) => term.highlightedBy),
            }
          : undefined,
    };

    prevStartIndex = startIndex;

    newParts.push(part);

    const indexOfNewPart = newParts.length - 1;
    allActiveTerms.forEach((term) => {
      term.partIndices.push(indexOfNewPart);

      if (part.highlighted) {
        term.partHtmlIds.push(part.highlighted.htmlId);
      }
    });

    currentMarker.endedTerms.forEach((term) => {
      activeTerms.delete(term);
    });
    currentMarker.startedTerms.forEach((term) => {
      activeTerms.add(term);
    });

    newParts.push(...currentMarker.emptyParts);

    // Keep previous if undefined or override.
    if (typeof currentMarker.cssClasses !== 'undefined') {
      activeCssClasses = _.clone(currentMarker.cssClasses);
    }
  });

  s.highlighted = {
    parts: newParts,
    terms: newTerms,
  };

  return addedTerms;
}
